import { MLAP_STAT_CARDS } from '@/config/mlap/statCards'
import { toCamelCase } from '@/helpers/stringUtils'

// This file holds display metadata only. Mlap::Dashboards::Catalog owns the keys, the widget options and the lower-is-better keys.

const STAT_CARD_OVERVIEW = {
  total_jobs: { execLabel: 'Jobs completed', sourceKey: 'service_titan', columnLabel: 'Jobs', freshness: ['servicetitan']},
  total_revenue: { execLabel: 'Revenue', sourceKey: 'service_titan', columnLabel: 'Revenue', freshness: ['servicetitan']},
  avg_distance: { execLabel: 'Average drive to a job', sourceKey: 'service_titan', columnLabel: 'Avg. drive', freshness: ['servicetitan']},
  gbp_impressions: { execLabel: 'Google profile views', columnLabel: 'Views', freshness: ['gbp']},
  gsc_clicks: { execLabel: 'Clicks from Google search', sourceKey: 'gsc', columnLabel: 'Clicks' },
  bing_clicks: { execLabel: 'Clicks from Bing search', sourceKey: 'bing', columnLabel: 'Clicks' },
  google_ads_spend: { execLabel: 'Google Ads spend' },
  google_ads_leads: { execLabel: 'Leads from Google Ads' },
  youtube_ads_spend: { execLabel: 'YouTube spend' },
  youtube_ads_leads: { execLabel: 'Leads from YouTube' },
  youtube_ads_views: { execLabel: 'YouTube views', sourceKey: 'youtube_ads' },
  microsoft_ads_spend: { execLabel: 'Microsoft Ads spend' },
  microsoft_ads_leads: { execLabel: 'Leads from Microsoft Ads' },
  meta_ads_spend: { execLabel: 'Facebook & Instagram spend' },
  meta_ads_leads: { execLabel: 'Leads from Facebook & Instagram' },
  nextdoor_ads_spend: { execLabel: 'Nextdoor spend', columnLabel: 'Spend' },
  nextdoor_ads_impressions: { execLabel: 'Nextdoor ad views', sourceKey: 'nextdoor_ads' },
  nextdoor_ads_clicks: { execLabel: 'Nextdoor ad clicks', sourceKey: 'nextdoor_ads', columnLabel: 'Clicks' },
  avg_position: { execLabel: 'Google search position', sourceKey: 'gsc', columnLabel: 'Position' },
  ga4_sessions: { execLabel: 'Website visits', sourceKey: 'ga4', columnLabel: 'Visits' },
  new_reviews: { execLabel: 'New reviews', sourceKey: 'reviews', columnLabel: 'Reviews' },
  avg_rating: { execLabel: 'Average rating', sourceKey: 'reviews', columnLabel: 'Rating' },
  avg_atgr: {
    execLabel: 'Map rank',
    execDescription: 'Average map rank across the geogrid points (lower is better).',
    columnLabel: 'Avg. rank',
    precision: 1,
    freshness: ['geogrid']
  },
  competitor_rating_gap: { execLabel: 'Rating vs competitors' },
  avg_solv: { execLabel: 'Top-3 map visibility', columnLabel: 'Top-3 share', freshness: ['geogrid']},
  lsa_spend: { execLabel: 'Local Services Ads spend', columnLabel: 'Spend' },
  lsa_leads: { execLabel: 'Leads from Local Services Ads', columnLabel: 'Leads' },
  lsa_charged_leads: { execLabel: 'Billed Local Services leads', sourceKey: 'lsa', columnLabel: 'Billed' }
}

const PAID_SOURCE = 'Google Ads, YouTube, Microsoft Ads, Meta, Nextdoor and Local Services Ads'
const LEAD_SOURCE = 'Google Ads, YouTube, Microsoft Ads, Meta and Local Services Ads'
const COST_PER_LEAD_NOTE = 'Spend from the channels that report leads, at mapped locations, divided by those leads. ' +
  'Territory spend is not included.'
const LEADS_NOTE = 'Google Ads and YouTube count conversions as leads, so a campaign tracking page views as ' +
  'conversions inflates this number.'

const paidSources = (paid) => (paid?.dataThrough || []).map((entry) => entry.source)
const leadSources = (paid) => paid?.paidCostPerLeadChannels || []

const paidMetric = (key, valueStem, attrs) => ({
  key,
  block: 'paid',
  sourceKey: 'paid_totals',
  freshnessFrom: paidSources,
  color: 'primary-light',
  valueKey: `${valueStem}${attrs.format === 'currency' ? 'Cents' : ''}`,
  prevValueKey: `prev${valueStem.charAt(0).toUpperCase()}${valueStem.slice(1)}${attrs.format === 'currency' ? 'Cents' : ''}`,
  growthKey: `${valueStem}Growth`,
  sparklineKey: `${valueStem}Daily`,
  ...attrs
})

const channelCostPerLead = (key, stem, { channel, displayName = channel, label = `${channel} Cost/Lead`, source, ...attrs }) => paidMetric(key, stem, {
  ratio: true,
  label,
  execLabel: `${displayName} cost per lead`,
  icon: 'fa-solid fa-coins',
  format: 'currency',
  source: channel,
  freshnessFrom: null,
  freshness: [source],
  description: `${channel} spend at mapped locations, divided by its leads over the selected dates. ` +
    'Territory spend is not included. Empty when there were no leads.',
  ...attrs
})

const PAID_METRICS = [
  paidMetric('paid_spend', 'paidSpend', {
    label: 'Ad Spend',
    execLabel: 'Ad spend',
    icon: 'fa-solid fa-sack-dollar',
    format: 'currency',
    source: 'All ad platforms',
    includesLsa: true,
    description: `Total ad spend across ${PAID_SOURCE} for the locations in view.`
  }),
  paidMetric('paid_leads', 'paidLeads', {
    label: 'Ad Leads',
    execLabel: 'Leads from ads',
    icon: 'fa-solid fa-user-plus',
    format: 'number',
    color: 'primary',
    source: 'All ad platforms',
    includesLsa: true,
    freshnessFrom: leadSources,
    description: `Leads reported by ${LEAD_SOURCE}. ${LEADS_NOTE}`
  }),
  paidMetric('paid_cost_per_lead', 'paidCostPerLead', {
    ratio: true,
    label: 'Cost/Lead',
    execLabel: 'Cost per lead',
    // The tile divides a narrower spend than the Ad spend tile, so it states its scope.
    execSubtitle: 'Lead-reporting channels · mapped locations',
    icon: 'fa-solid fa-coins',
    format: 'currency',
    source: 'All ad platforms',
    includesLsa: true,
    freshnessFrom: leadSources,
    description: `${COST_PER_LEAD_NOTE} ${LEADS_NOTE}`
  }),
  channelCostPerLead('google_ads_cost_per_lead', 'googleAdsCostPerLead', { channel: 'Google Ads', source: 'google_ads' }),
  channelCostPerLead('youtube_ads_cost_per_lead', 'youtubeAdsCostPerLead', { channel: 'YouTube', source: 'youtube_ads' }),
  channelCostPerLead('microsoft_ads_cost_per_lead', 'microsoftAdsCostPerLead', { channel: 'Microsoft Ads', source: 'microsoft_ads' }),
  channelCostPerLead('meta_ads_cost_per_lead', 'metaAdsCostPerLead', {
    channel: 'Meta', displayName: 'Facebook & Instagram', source: 'meta_ads'
  }),
  channelCostPerLead('lsa_cost_per_lead', 'lsaCostPerLead', {
    channel: 'Local Services Ads',
    displayName: 'Local Services',
    label: 'LSA Cost/Lead',
    source: 'lsa',
    columnLabel: 'Per lead',
    includesLsa: true,
    description: 'Local Services Ads spend divided by all leads of the mapped LSA accounts over the selected dates. ' +
      'The LSA Cost/Lead card on Details divides by charged leads only. Territory spend is not included. Empty when there were no leads.'
  })
]

// YouTube is part of Google Ads: Google Ads location spend includes its VIDEO campaigns.
export const AD_CHANNELS = [
  { key: 'google_ads', label: 'Google Ads', spend: 'google_ads_spend', leads: 'google_ads_leads', costPerLead: 'google_ads_cost_per_lead' },
  {
    key: 'youtube_ads',
    label: 'YouTube',
    spend: 'youtube_ads_spend',
    leads: 'youtube_ads_leads',
    costPerLead: 'youtube_ads_cost_per_lead',
    breakdownOf: 'google_ads'
  },
  { key: 'microsoft_ads', label: 'Microsoft Ads', spend: 'microsoft_ads_spend', leads: 'microsoft_ads_leads', costPerLead: 'microsoft_ads_cost_per_lead' },
  { key: 'meta_ads', label: 'Facebook & Instagram', spend: 'meta_ads_spend', leads: 'meta_ads_leads', costPerLead: 'meta_ads_cost_per_lead' },
  { key: 'nextdoor_ads', label: 'Nextdoor', spend: 'nextdoor_ads_spend', leads: null, costPerLead: null },
  { key: 'lsa', label: 'Local Services Ads', spend: 'lsa_spend', leads: 'lsa_leads', costPerLead: 'lsa_cost_per_lead' }
]

// Each channel reads the paid block, like the Ad spend tile, so the channels add up to the totals.
const CHANNEL_METRICS = AD_CHANNELS.flatMap((channel) => [['spend', channel.spend], ['leads', channel.leads]]
  .filter(([, key]) => key)
  .map(([measure, key]) => {
    const { label, icon, source, description } = MLAP_STAT_CARDS.find((card) => card.key === key)
    return paidMetric(key, toCamelCase(key), {
      label,
      icon,
      source,
      description,
      ...STAT_CARD_OVERVIEW[key],
      format: measure === 'spend' ? 'currency' : 'number',
      freshnessFrom: null,
      freshness: [channel.key],
      includesLsa: channel.key === 'lsa'
    })
  }))

const OVERVIEW_METRICS = Object.freeze(Object.fromEntries([
  ...MLAP_STAT_CARDS.map((card) => [card.key, { ...card, block: 'summary', sourceKey: null, ...STAT_CARD_OVERVIEW[card.key] }]),
  ...[...PAID_METRICS, ...CHANNEL_METRICS].map((metric) => [metric.key, metric])
]))

export function overviewMetric(key) {
  return OVERVIEW_METRICS[key] ?? null
}

// Metrics without a list (reviews, search) show no date.
export const SOURCE_FRESHNESS = {
  youtube_ads: ['youtube_ads'],
  nextdoor_ads: ['nextdoor_ads'],
  lsa: ['lsa_rails']
}

// The rows of an `additive` measure add up to `total`. The cost per lead bars only compare with it.
export const CHANNEL_MEASURES = {
  spend: { field: 'spend', label: 'Ad spend by channel', shortLabel: 'Ad spend', total: 'paid_spend', additive: true },
  leads: { field: 'leads', label: 'Leads by channel', shortLabel: 'Leads', total: 'paid_leads', additive: true },
  cost_per_lead: { field: 'costPerLead', label: 'Cost per lead by channel', shortLabel: 'Cost per lead', total: 'paid_cost_per_lead', additive: false }
}

export const LSA_DEFINITION_LABEL = 'LSA definition differs'

